import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { EnergyAnalysisCategoryTotal, EnergyAnalysisTotalChartType } from "../model/types";

import { TOTAL_CHART_TYPE_ICONS } from "../lib/chartTypeIcons";
import { ENERGY_ANALYSIS_TOTAL_CHART_TYPE } from "../model/constants";
import { EnergyAnalysisChartTypeMenu } from "./EnergyAnalysisChartTypeMenu";
import { EnergyAnalysisKpiChart } from "./EnergyAnalysisKpiChart";
import { EnergyAnalysisSectionTitle } from "./EnergyAnalysisSectionTitle";
import { EnergyAnalysisShareLegend } from "./EnergyAnalysisShareLegend";
import { EnergyAnalysisTotalChartCanvas } from "./EnergyAnalysisTotalChartCanvas";

const TOTAL_CHART_TYPES: EnergyAnalysisTotalChartType[] = [
  ENERGY_ANALYSIS_TOTAL_CHART_TYPE.doughnut,
  ENERGY_ANALYSIS_TOTAL_CHART_TYPE.kpi,
  ENERGY_ANALYSIS_TOTAL_CHART_TYPE.barVertical,
  ENERGY_ANALYSIS_TOTAL_CHART_TYPE.barHorizontal,
];

type EnergyAnalysisTotalChartProps = {
  categoryTotals: EnergyAnalysisCategoryTotal[];
  chartType: EnergyAnalysisTotalChartType;
  onChartTypeChange: (chartType: EnergyAnalysisTotalChartType) => void;
  snapshotValueMax: number;
  title: string;
  totalValue: number;
  unit: string;
};

export const EnergyAnalysisTotalChart = ({
  categoryTotals,
  chartType,
  onChartTypeChange,
  snapshotValueMax,
  title,
  totalValue,
  unit,
}: EnergyAnalysisTotalChartProps) => {
  const { t } = useTranslation();
  const isRing = chartType === ENERGY_ANALYSIS_TOTAL_CHART_TYPE.doughnut;
  const isKpi = chartType === ENERGY_ANALYSIS_TOTAL_CHART_TYPE.kpi;
  return (
    <Stack className="w-full flex-col items-stretch gap-3">
      <Stack className="w-full items-center justify-between gap-2">
        <EnergyAnalysisSectionTitle iconName="Piechart" title={title} />
        <EnergyAnalysisChartTypeMenu
          onChange={onChartTypeChange}
          options={TOTAL_CHART_TYPES.map((type) => ({
            iconName: TOTAL_CHART_TYPE_ICONS[type],
            label: t(`BuildingProfile.energyAnalysis.chartTypes.${type}`),
            value: type,
          }))}
          value={chartType}
        />
      </Stack>
      {isKpi ? (
        <EnergyAnalysisKpiChart items={categoryTotals} unit={unit} />
      ) : (
        <EnergyAnalysisTotalChartCanvas
          categoryTotals={categoryTotals}
          chartType={chartType}
          snapshotValueMax={snapshotValueMax}
          showCenter={isRing}
          totalValue={totalValue}
          unit={unit}
        />
      )}
      {isRing ? <EnergyAnalysisShareLegend items={categoryTotals} unit={unit} /> : null}
    </Stack>
  );
};
